import type { PlanningPermissionApplication } from '@/models/PlanningPermissionApplication';
import { getI18n } from './i18n';
import { useBrowserStorage } from '@kp/geofy-vuejs-lib/tools/browser_local_storage.js';
import dayjs, { Dayjs } from 'dayjs';

/**
 * Creates a function that can only be called "debounced", means, only all n milliseconds.
 *
 * @param      {Function}  fn      The function to execute
 * @param      {Number}    millis  The nr of milliseconds in which the function is NOT called again
 * @return     {Function}  a new Function that can rapidly be called, but is only invoked max all n milliseconds.
 */
export function debounce(fn: (...args: any) => void, millis = 350) {
    // hoisted timeout variable
    let timeout: number | null = null;
    const callbackFn = () => (timeout = null);

    return function (...args: any) {
        if (timeout) {
            return;
        }
        timeout = setTimeout(callbackFn, millis);
        fn(...args);
    };
}

export function intToFilesizeStr(bytes: number) {
    if (bytes / 1000000000 > 1) {
        return (bytes / 1000000000).toFixed(2) + ' GB';
    }
    if (bytes / 1000000 > 1) {
        return (bytes / 1000000).toFixed(2) + ' MB';
    }
    if (bytes / 1000 > 1) {
        return (bytes / 1000).toFixed(2) + ' KB';
    }
    return bytes + ' B';
}

export function todoAlert() {
    alert('TODO');
}

/**
 * Tries to make sense of the given "Error", which may be a string,
 * an error object, some kind of response.... you name it.
 * @param {any} error The error "thingie"
 * @return A string representing the error msg.
 */
export function errToStr(error: any, ignoreCode: boolean = false): string {
    const vueI18n = getI18n();
    let msg = vueI18n.t('unknown_error');

    if (error) {
        // Try to extract a data property:
        const data = error?.response?.data || error?.response?.response?.data;

        if (data?.error?.msg) {
            if (ignoreCode) {
                msg = `${data.error.msg}`;
            } else {
                msg = `${data.error.code}: ${data.error.msg}`;
            }
        } else if (error?.response?.responseText) {
            const obj = JSON.parse(error.response.responseText);
            if (obj?.error?.msg) {
                if (ignoreCode) {
                    msg = `${obj.error.msg}`;
                } else {
                    msg = `${obj.error.code}: ${obj.error.msg}`;
                }
            } else {
                msg = String(error.response.responseText);
            }
        } else if (error.responseText) {
            const obj = JSON.parse(error.responseText);
            if (obj?.error?.msg) {
                if (ignoreCode) {
                    msg = `${obj.error.msg}`;
                } else {
                    msg = `${obj.error.code}: ${obj.error.msg}`;
                }
            } else {
                msg = String(error.responseText);
            }
        } else if (typeof error === 'string') {
            try {
                const obj = JSON.parse(error);
                if (obj?.error?.msg) {
                    if (ignoreCode) {
                        msg = `${obj.error.msg}`;
                    } else {
                        msg = `${obj.error.code}: ${obj.error.msg}`;
                    }
                } else {
                    msg = String(error);
                }
            } catch (x) {
                msg = String(error);
            }
        } else {
            msg = String(error);
        }
    }
    return msg;
}

export function fileExtToMime(ext: string) {
    switch (ext.toLocaleLowerCase()) {
        case 'pdf':
            return 'application/pdf';
        case 'doc':
        case 'docx':
            return 'application/msword';
        case 'ppt':
        case 'pptx':
            return 'application/vnd.ms-powerpoint';
        case 'xls':
            return 'application/vnd.ms-excel';
        case 'xlsx':
            return 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';
        case 'jpg':
        case 'jpeg':
            return 'image/jpeg';
        case 'png':
            return 'image/png';
        default:
            return 'application/octet-stream';
    }
}

export function capitalize(s: string | null): string {
    s = s || '';
    return s[0].toUpperCase() + s.slice(1);
}

/**
 * This function stores the fact that a specific PPA has once reached the "Abschluss" route:
 * We need this information to adapt the field validation: We want to disable auto-validation
 * as long as the user did not reach the "Abschluss" route, as it seems a bit too early in the process
 * to validate all the form fields in the previous forms: Every field is red, but the user has not yet
 * had a chance to fill it out.
 *
 * But as soon as the user once reached the Abschluss form, we take it that he has at least opened the other forms
 * once, so we can then auto-validate the form fields from now on.
 *
 * This information is solely stored in the user's local browser storage, and NOT persisted to the backend.
 * @param gesuch
 */
export function markPpaSubmitRouteReached(gesuch: PlanningPermissionApplication) {
    const { setStorageValue } = useBrowserStorage('eplan.ppaSubmitRoute');
    if (!hasPpaReachedSubmitRoute(gesuch)) {
        setStorageValue(String(gesuch.id), new Date().toISOString());
    }
}

/**
 * This function is the counterpart of the @see markPpaSubmitRouteReached: It checks if a given
 * PPA has already reached the "Abschluss" route.
 * @see markPpaSubmitRouteReached
 * @param gesuch
 * @returns
 */
export function hasPpaReachedSubmitRoute(gesuch: PlanningPermissionApplication) {
    const { getStorageValue } = useBrowserStorage('eplan.ppaSubmitRoute');
    return Boolean(getStorageValue(String(gesuch.id), false));
}

/**
 * Formats a DayJS object if it is valid, otherwise returns an emtpy string by default.
 *
 * @param      {(Dayjs|null)}  dateObj            The Dayjs object
 * @param      {string}        format             The format
 * @param      {string}        [invalidValue='']  The string that is putput if the dayjs object is null or invalid
 * @return     {string}        Formatted date string or the defined invalid string ('' by default)
 */
export function formatDayjsIf(dateObj: Dayjs | null, format: string, invalidValue = ''): string {
     if (dateObj && dateObj.isValid()) {
        return dateObj.format(format);
    } else {
        return invalidValue;
    }
}